/**
 * The single source of truth for Clearistic brand colours.
 *
 * Both Material-UI themes (`clearisticTheme`, `clearisticThemeDark`) and the
 * Backstage UI token overrides (`clearistic-bui.css`) are built from these
 * values, so the two component libraries render the same colours.
 *
 * Every foreground/background pairing used by the themes is asserted to meet
 * WCAG 2.1 AA (4.5:1 for body text, 3:1 for large text and non-text UI) by
 * `contrast.test.ts`. If you change a value here, run that test.
 *
 * Roles, rather than raw hex, are what the themes consume:
 *  - `brand.500` is the Clearistic blue. It has a 2.53:1 contrast against white,
 *    so it is only ever used for graphics and fills — never for text on a light
 *    surface, and never behind white text.
 *  - Light surfaces take their text and interactive colour from `brand.700`+.
 *  - Dark surfaces take theirs from `brand.300`/`brand.400`, and solid brand
 *    fills carry *dark* text (`neutral.950`), the same convention Backstage UI
 *    uses for its own dark-mode accent.
 */

/** Clearistic blue, ramped from the #00AEEF brand colour. */
export const brand = {
  50: '#e6f7fe',
  100: '#b3e7fa',
  200: '#80d7f7',
  300: '#4dc6f4',
  400: '#1ab6f1',
  500: '#00aeef',
  600: '#0094cb',
  700: '#00719b',
  800: '#005778',
  900: '#003d54',
} as const;

/** Teal accent, ramped from the #7df3e1 brand accent. */
export const accent = {
  100: '#c9f9f0',
  200: '#7df3e1',
  300: '#3fd9c2',
  400: '#14b8a6',
  500: '#0d9488',
  600: '#0f766e',
  700: '#115e59',
  800: '#134e4a',
  900: '#0f3b38',
} as const;

/** Blue-tinted neutrals, so greys sit in the same family as the brand blue. */
export const neutral = {
  0: '#ffffff',
  25: '#f7f9fb',
  50: '#f1f4f7',
  100: '#e4e9ee',
  200: '#cfd7e0',
  300: '#aab6c3',
  400: '#7d8b9a',
  500: '#5c6a79',
  600: '#45525f',
  700: '#334150',
  800: '#26333f',
  900: '#1a2632',
  950: '#111b24',
} as const;

/**
 * Status colours, resolved per mode. `main` is a fill or icon colour, `fg` is
 * text on the mode's own surfaces, `bg` is a subdued container, and `fgOnBg` is
 * text sitting on that container.
 */
export const status = {
  light: {
    error: { main: '#c62828', fg: '#a01b1b', bg: '#fdecea', fgOnBg: '#8c1d18', border: '#f0a9a4' },
    warning: { main: '#b45309', fg: '#96430a', bg: '#fff5e6', fgOnBg: '#7c2d12', border: '#f0c48a' },
    success: { main: '#15803d', fg: '#116932', bg: '#e9f9ef', fgOnBg: '#14532d', border: '#8ad3a5' },
    info: { main: '#0369a1', fg: '#075985', bg: '#e8f4fd', fgOnBg: '#0c4a6e', border: '#94c5e8' },
  },
  dark: {
    error: { main: '#f87171', fg: '#fca5a5', bg: '#3b1518', fgOnBg: '#fecaca', border: '#7f2528' },
    warning: { main: '#fbbf24', fg: '#fcd34d', bg: '#3a2a0c', fgOnBg: '#fde68a', border: '#8a6414' },
    success: { main: '#4ade80', fg: '#86efac', bg: '#10301d', fgOnBg: '#bbf7d0', border: '#1f6b3c' },
    info: { main: '#60a5fa', fg: '#93c5fd', bg: '#16283f', fgOnBg: '#bfdbfe', border: '#2b5288' },
  },
} as const;

/** Per-mode surface and text roles. */
export const surfaces = {
  light: {
    app: neutral[50],
    paper: neutral[0],
    raised: neutral[25],
    /** Hairline dividers. Decorative, so only perceptibility matters. */
    border: neutral[100],
    borderStrong: neutral[200],
    /** Borders of interactive controls (inputs, checkboxes) — must clear 3:1. */
    borderInteractive: neutral[400],
    fg: '#16202b',
    fgSecondary: neutral[500],
    fgDisabled: neutral[400],
    /** Interactive / text brand colour that clears AA on `app` and `paper`. */
    interactive: brand[700],
    interactiveHover: brand[800],
    /** Text placed on a solid `interactive` fill. */
    onInteractive: neutral[0],
    /** Dark chrome (sidebar, backdrop) is shared with the dark theme. */
    chrome: neutral[900],
    onChrome: neutral[0],
  },
  dark: {
    app: neutral[950],
    paper: neutral[900],
    raised: neutral[800],
    /** Hairline dividers. Decorative, so only perceptibility matters. */
    border: neutral[700],
    borderStrong: neutral[600],
    /** Borders of interactive controls (inputs, checkboxes) — must clear 3:1. */
    borderInteractive: neutral[400],
    fg: '#eef2f6',
    fgSecondary: neutral[300],
    fgDisabled: neutral[400],
    interactive: brand[400],
    interactiveHover: brand[300],
    /** Solid brand fills in dark mode carry dark text, not white. */
    onInteractive: neutral[950],
    chrome: neutral[950],
    onChrome: '#eef2f6',
  },
} as const;
